<template>
  <div id="display_tab" class="my-6">
    <NotificationPopup
      v-if="widget.edited"
      status="warning"
      title="Changes Unpublished"
      text="To view your updates on the widget, please save and publish them."
      class="mb-6"
    />
    <div class="tab-wrapper-primary">
      <div class="tab-heading-primary">{{ changeCase('display', ElementE.TITLE) }}</div>
      <div class="flex flex-col gap-4 p-6">
        <h4 class="text-lg font-medium">{{ changeCase('layout', ElementE.TITLE) }}</h4>
      </div>
      <div class="grid grid-cols-4 gap-4 px-6 pb-6 desktop:grid-cols-2 laptop:grid-cols-1">
        <LayoutCard
          id="search"
          :label="(changeCase('search', ElementE.LABEL) as string)"
          :isSelected="search"
          @select="selectLayout"
        >
          <template #icon>
            <SearchLayoutIcon />
          </template>
        </LayoutCard>

        <LayoutCard
          id="matrixSearch"
          :label="(changeCase('search & matrix', ElementE.LABEL) as string)"
          :isSelected="matrixSearch"
          @select="selectLayout"
        >
          <template #icon>
            <SearchMatrixLayoutIcon />
          </template>
        </LayoutCard>

        <LayoutCard
          id="matrix"
          :label="(changeCase('matrix', ElementE.LABEL) as string)"
          :isSelected="matrix"
          @select="selectLayout"
        >
          <template #icon>
            <MatrixLayoutIcon />
          </template>
          <template #extra>
            <CheckboxSlide
              label="Display matrix on mobile"
              :disabled="!matrix"
              :isChecked="displayMatrixOnMobile"
              size="xs"
              @update:isChecked="enableDisplayMatrixOnMobile"
            />
          </template>
        </LayoutCard>

        <LayoutCard
          id="cardMatrix"
          :label="(changeCase('card matrix', ElementE.LABEL) as string)"
          :isSelected="cardMatrix"
          @select="selectLayout"
        >
          <template #icon>
            <CardMatrixLayoutIcon :layout="cardMatrixRoomLayout" />
          </template>
          <template #extra>
            <div class="flex flex-col gap-2">
              <CheckboxSlide
                :label="(changeCase('show search bar', ElementE.LABEL) as string)"
                :disabled="!cardMatrix"
                :isChecked="cardMatrixShowSearch"
                size="xs"
                @update:isChecked="toggleCardMatrixShowSearch"
              />
              <div>
                <CheckboxSlide
                  :label="(changeCase('show property boards on load', ElementE.LABEL) as string)"
                  :disabled="!cardMatrix || !cardMatrixShowSearch"
                  :isChecked="cardMatrixShowPropertyBoards"
                  size="xs"
                  @update:isChecked="toggleCardMatrixShowPropertyBoards"
                />
                <p v-if="cardMatrix && !cardMatrixShowSearch" class="text-[10px] text-gray-400 mt-0.5 ml-[42px]">
                  {{ changeCase('required when the search bar is hidden', ElementE.LABEL) }}
                </p>
              </div>
              <div class="pl-4">
                <CheckboxSlide
                  :label="(changeCase('show teaser text before search', ElementE.LABEL) as string)"
                  :disabled="!cardMatrix || cardMatrixShowPropertyBoards"
                  :isChecked="cardMatrixShowTeaserText"
                  size="xs"
                  @update:isChecked="toggleCardMatrixShowTeaserText"
                />
                <p v-if="cardMatrix && cardMatrixShowPropertyBoards" class="text-[10px] text-gray-400 mt-0.5 ml-[42px]">
                  {{ changeCase('not available when property boards are shown on load', ElementE.LABEL) }}
                </p>
              </div>
              <CheckboxSlide
                :label="(changeCase('show availability matrix', ElementE.LABEL) as string)"
                :disabled="!cardMatrix"
                :isChecked="cardMatrixShowMatrix"
                size="xs"
                @update:isChecked="toggleCardMatrixShowMatrix"
              />
              <div class="pl-4 flex flex-col gap-2">
                <CheckboxSlide
                  :label="(changeCase('open matrix when card expands', ElementE.LABEL) as string)"
                  :disabled="!cardMatrix || !cardMatrixShowMatrix"
                  :isChecked="cardMatrixShowMatrixOnLoad"
                  size="xs"
                  @update:isChecked="toggleCardMatrixShowMatrixOnLoad"
                />
                <CheckboxSlide
                  :label="(changeCase('show prices in matrix', ElementE.LABEL) as string)"
                  :disabled="!cardMatrix || !cardMatrixShowMatrix || (cardMatrixShowPrices && !cardMatrixShowRoomCount)"
                  :isChecked="cardMatrixShowPrices"
                  size="xs"
                  @update:isChecked="toggleCardMatrixShowPrices"
                />
                <CheckboxSlide
                  :label="(changeCase('show room count in matrix', ElementE.LABEL) as string)"
                  :disabled="!cardMatrix || !cardMatrixShowMatrix || (cardMatrixShowRoomCount && !cardMatrixShowPrices)"
                  :isChecked="cardMatrixShowRoomCount"
                  size="xs"
                  @update:isChecked="toggleCardMatrixShowRoomCount"
                />
                <p v-if="cardMatrix && cardMatrixShowMatrix && (!cardMatrixShowPrices || !cardMatrixShowRoomCount)" class="text-[10px] text-gray-400 mt-0.5 ml-[42px]">
                  {{ changeCase('prices or room count must stay on so matrix cells are not empty', ElementE.LABEL) }}
                </p>
                <CheckboxSlide
                  :label="(changeCase('show sold-out label in matrix', ElementE.LABEL) as string)"
                  :disabled="!cardMatrix || !cardMatrixShowMatrix"
                  :isChecked="cardMatrixShowAvailabilityText"
                  size="xs"
                  @update:isChecked="toggleCardMatrixShowAvailabilityText"
                />
                <CheckboxSlide
                  :label="(changeCase('start matrix collapsed on mobile', ElementE.LABEL) as string)"
                  :disabled="!cardMatrix || !cardMatrixShowMatrix"
                  :isChecked="cardMatrixMobileDefaultCollapsed"
                  size="xs"
                  @update:isChecked="toggleCardMatrixMobileDefaultCollapsed"
                />
                <p v-if="cardMatrix && !cardMatrixShowMatrix" class="text-[10px] text-gray-400 mt-0.5 ml-[42px]">
                  {{ changeCase('not available when the availability matrix is hidden', ElementE.LABEL) }}
                </p>
              </div>
              <div>
                <CheckboxSlide
                  :label="(changeCase('pre-load properties', ElementE.LABEL) as string)"
                  :disabled="!cardMatrix || !cardMatrixShowSearch"
                  :isChecked="cardMatrixPreloadProperties"
                  size="xs"
                  @update:isChecked="toggleCardMatrixPreloadProperties"
                />
                <p v-if="cardMatrix && !cardMatrixShowSearch" class="text-[10px] text-gray-400 mt-0.5 ml-[42px]">
                  {{ changeCase('required when the search bar is hidden', ElementE.LABEL) }}
                </p>
              </div>
              <div class="flex flex-wrap items-center gap-x-3 gap-y-2 mt-1">
                <span class="text-sm text-gray-600 whitespace-nowrap">{{ changeCase('room card layout', ElementE.LABEL) }}</span>
                <div class="flex rounded-md border border-gray-300 overflow-hidden shrink-0">
                  <button
                    class="px-3 py-1 text-xs font-medium transition-colors"
                    :class="cardMatrixRoomLayout === 'horizontal'
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'"
                    :disabled="!cardMatrix"
                    @click="setCardMatrixRoomLayout('horizontal')"
                  >
                    {{ changeCase('horizontal', ElementE.BUTTON) }}
                  </button>
                  <button
                    class="px-3 py-1 text-xs font-medium transition-colors border-l border-gray-300"
                    :class="cardMatrixRoomLayout === 'vertical'
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'"
                    :disabled="!cardMatrix"
                    @click="setCardMatrixRoomLayout('vertical')"
                  >
                    {{ changeCase('vertical', ElementE.BUTTON) }}
                  </button>
                </div>
              </div>
              <CheckboxSlide
                :label="(changeCase('show pricing teaser on room cards', ElementE.LABEL) as string)"
                :disabled="!cardMatrix"
                :isChecked="cardMatrixShowTeaserPricing"
                size="xs"
                @update:isChecked="toggleCardMatrixShowTeaserPricing"
              />
              <CheckboxSlide
                :label="(changeCase('hide rates inside room cards', ElementE.LABEL) as string)"
                :disabled="!cardMatrix"
                :isChecked="cardMatrixRoomCardHideRates"
                size="xs"
                @update:isChecked="toggleCardMatrixRoomCardHideRates"
              />
            </div>
          </template>
        </LayoutCard>
      </div>    
      <div class="flex flex-col gap-4 p-6">
        <h4 class="text-lg font-medium mb-2">{{ changeCase('widget customisation', ElementE.TITLE) }}</h4>
        <!-- Show Property Filter toggle - only visible when Search is active -->
        <div class="-mt-2">
          <CheckboxSlide
            label="Show Property Filter"
            :disabled="!search && !matrixSearch"
            :isChecked="showPropertyFilter"
            size="xs"
            @update:isChecked="enableShowPropertyFilter"
          />
        </div>
        <div class="w-1/2">
          <EditableDropdown
            :values="showDays"
            :defaultValue="String(widget.config?.display_days || 7)"
            label="Matrix Display Days"
            placeholder="Enter days or select from list"
            class="z-20"
            :min="1"
            :max="15"
            :validateAsNumber="true"
            @select="changeDisplayDays"
          />
        </div>
        <div class="w-1/2">
          <EditableDropdown
            :values="showDays"
            :defaultValue="String(widget.config?.default_night_count || 7)"
            label="Default Night Count"
            placeholder="Enter nights or select from list"
            :min="1"
            :max="15"
            :validateAsNumber="true"
            @select="changeDefaultNightCount"
          />
        </div>
      </div>

      <!-- Deals For You display settings -->
      <section class="p-6 max-w-lg">
        <h4 class="text-lg font-medium mb-2">{{ changeCase('"deals for you" display settings', ElementE.TITLE) }}</h4>
        <div class="overflow-hidden rounded-lg border border-gray-300 shadow-sm">
          <div class="table-header bg-gray-200 flex">
            <div class="px-4 py-2 text-sm font-semibold text-gray-900 flex-[3] border-r border-gray-300">Display Location / Element</div>
            <div class="px-4 py-2 text-sm font-semibold text-gray-900 flex-1 border-r border-gray-300">Specials</div>
            <div class="px-4 py-2 text-sm font-semibold text-gray-900 flex-1">MinLos</div>
          </div>
          <div class="table-body">
            <div v-show="!search" class="flex border-t border-gray-300">
              <div class="px-4 py-2 text-sm text-gray-900 flex-[3] flex items-center border-r border-gray-300">Matrix Row Header</div>
              <div class="px-4 py-2 text-center flex-1 flex items-center justify-center border-r border-gray-300">
                <BaseCheckbox
                  :checked="dealsSettings.specials.matrix_row_header"
                  :disabled="!isSpecialsEnabled"
                  @update:checked="updateDealsSettings($event, DealsSettingsType.SPECIALS, DealsSettingsLocation.MATRIX_ROW_HEADER)"
                />
              </div>
              <div class="px-4 py-2 text-center flex-1 flex items-center justify-center">
                <BaseCheckbox
                  :checked="dealsSettings.minlos.matrix_row_header"
                  @update:checked="updateDealsSettings($event, DealsSettingsType.MINLOS, DealsSettingsLocation.MATRIX_ROW_HEADER)"
                />
              </div>
            </div>
            <div v-show="!search" class="flex border-t border-gray-300">
              <div class="px-4 py-2 text-sm text-gray-900 flex-[3] flex items-center border-r border-gray-300">Matrix Cell</div>
              <div class="px-4 py-2 text-center flex-1 flex items-center justify-center border-r border-gray-300">
                <BaseCheckbox
                  :checked="dealsSettings.specials.matrix_cell"
                  :disabled="!isSpecialsEnabled"
                  @update:checked="updateDealsSettings($event, DealsSettingsType.SPECIALS, DealsSettingsLocation.MATRIX_CELL)"
                />
              </div>
              <div class="px-4 py-2 text-center flex-1 flex items-center justify-center">
                <BaseCheckbox
                  :checked="dealsSettings.minlos.matrix_cell"
                  @update:checked="updateDealsSettings($event, DealsSettingsType.MINLOS, DealsSettingsLocation.MATRIX_CELL)"
                />
              </div>
            </div>
            <div class="flex border-t border-gray-300">
              <div class="px-4 py-2 text-sm text-gray-900 flex-[3] flex items-center border-r border-gray-300">Search Results Button</div>
              <div class="px-4 py-2 text-center flex-1 flex items-center justify-center border-r border-gray-300">
                <BaseCheckbox
                  :checked="dealsSettings.specials.search_results_button"
                  :disabled="!isSpecialsEnabled"
                  @update:checked="updateDealsSettings($event, DealsSettingsType.SPECIALS, DealsSettingsLocation.SEARCH_RESULTS_BUTTON)"
                />
              </div>
              <div class="px-4 py-2 text-center flex-1 flex items-center justify-center">
                <BaseCheckbox
                  :checked="dealsSettings.minlos.search_results_button"
                  @update:checked="updateDealsSettings($event, DealsSettingsType.MINLOS, DealsSettingsLocation.SEARCH_RESULTS_BUTTON)"
                />
              </div>
            </div>
            <div class="flex border-t border-gray-300">
              <div class="px-4 py-2 text-sm text-gray-900 flex-[3] flex items-center border-r border-gray-300">Teaser Below Photo Carousel</div>
              <div class="px-4 py-2 text-center flex-1 flex items-center justify-center border-r border-gray-300">
                <BaseCheckbox
                  :checked="dealsSettings.specials.teaser_below_photo_carousel"
                  :disabled="true"
                  @update:checked="updateDealsSettings($event, DealsSettingsType.SPECIALS, DealsSettingsLocation.TEASER_BELOW_PHOTO_CAROUSEL)"
                />
              </div>
              <div class="px-4 py-2 text-center flex-1 flex items-center justify-center">
                <BaseCheckbox
                  :checked="dealsSettings.minlos.teaser_below_photo_carousel"
                  @update:checked="updateDealsSettings($event, DealsSettingsType.MINLOS, DealsSettingsLocation.TEASER_BELOW_PHOTO_CAROUSEL)"
                />
              </div>
            </div>
            <div class="flex border-t border-gray-300">
              <div class="px-4 py-2 text-sm text-gray-900 flex-[3] flex items-center border-r border-gray-300">Details Pop-up (Deals For You)</div>
              <div class="px-4 py-2 text-center flex-1 flex items-center justify-center border-r border-gray-300">
                <BaseCheckbox
                  :checked="dealsSettings.specials.details_pop_up"
                  :disabled="!isSpecialsEnabled"
                  @update:checked="updateDealsSettings($event, DealsSettingsType.SPECIALS, DealsSettingsLocation.DETAILS_POP_UP)"
                />
              </div>
              <div class="px-4 py-2 text-center flex-1 flex items-center justify-center">
                <BaseCheckbox
                  :checked="dealsSettings.minlos.details_pop_up"
                  @update:checked="updateDealsSettings($event, DealsSettingsType.MINLOS, DealsSettingsLocation.DETAILS_POP_UP)"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div
      id="matrix-view"
      class="tab-wrapper-primary mt-6"
      v-if="showMatrixView"
    >
      <div class="tab-heading-primary">{{ changeCase('customise matrix', ElementE.TITLE) }}</div>

      <div class="flex flex-col gap-4 p-6">
        <CheckboxSlide
          class="z-30"
          :is-checked="showPropertyThumbnail"
          @change="showPropThumbnailChange"
          info="some info"
          label="show property thumbnail"
          tooltipTxt="Display a small preview image of the property displayed alongside its details."
        />

        <div class="h-px border border-dashed border-gray-300"></div>

        <DropdownMenu
          v-if="widget.config?.matrix_mode"
          :values="showVariant"
          :defaultSelect="MatrixDisplayDetailsE[widget.config?.matrix_mode as keyof typeof MatrixDisplayDetailsE] || 'Select value'"
          label="Matrix display details"
          @select="showAviableField"
        />

        <div class="flex justify-between gap-4">
          <div v-if="showAviable" class="relative w-1/2 mt-2">
            <label class="label-primary" for="">
              {{ changeCase('available text', ElementE.LABEL) }}
            </label>
            <input
              v-on:change="updateWidget"
              v-model.trim="widget.config.available_text"
              class="input-primary"
              :class="
                errors.availableText && 'border-critical'
              "
              type="text"
              :placeholder="(changeCase('enter text', ElementE.PLACEHOLDER) as string)"
              @blur="
                validateField('availableText', widget.config.available_text)
              "
            />
            <div v-if="errors.availableText">
              <div class="absolute top-3.5 right-3">
                <ErrorIcon />
              </div>
              <p class="mt-2 text-xs text-critical-main">
                {{ errors.availableText }}
              </p>
            </div>
          </div>

          <div class="relative w-1/2 mt-2">
            <label class="label-primary" for="">
              {{ changeCase('text when no rooms available', ElementE.LABEL) }}
            </label>
            <input
              v-on:change="updateWidget"
              v-model.trim="widget.config.unavailable_text"
              class="input-primary"
              :class="
                errors.unavailableText && 'border-critical'
              "
              type="text"
              :placeholder="(changeCase('enter text', ElementE.PLACEHOLDER) as string)"
              @blur="
                validateField('unavailableText', widget.config.unavailable_text)
              "
            />
            <div v-if="errors.unavailableText">
              <div class="absolute top-3.5 right-3">
                <ErrorIcon />
              </div>
              <p class="mt-2 text-xs text-critical-main">
                {{ errors.unavailableText }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import CheckboxSlide from "../inputs/CheckboxSlide.vue";
import LayoutCard from "../inputs/LayoutCard.vue";
import NotificationPopup from "../notifications/NotificationPopup.vue";
import { WidgetI, HtmlEvent } from "../../interfaces/interfaces";
import ErrorIcon from "../icons/ErrorIcon.vue";
import SearchLayoutIcon from "../icons/SearchLayoutIcon.vue";
import SearchMatrixLayoutIcon from "../icons/SearchMatrixLayoutIcon.vue";
import MatrixLayoutIcon from "../icons/MatrixLayoutIcon.vue";
import CardMatrixLayoutIcon from "../icons/CardMatrixLayoutIcon.vue";
import { showDays, showVariant, IShowDay, IShowVariant } from "./constants";
import DropdownMenu from "../inputs/DropdownMenu.vue";
import { changeCase } from "../../helpers";
import { ElementE, MatrixDisplayDetailsE } from '../../enums'
import BaseCheckbox from '../inputs/BaseCheckbox.vue';
import EditableDropdown from '../inputs/EditableDropdown.vue';

interface ErrorI {
  availableText: string;
  unavailableText: string;
  [key: string]: string;
}

enum DealsSettingsType {
  SPECIALS = 'specials',
  MINLOS = 'minlos',
}

enum DealsSettingsLocation {
  MATRIX_ROW_HEADER = 'matrix_row_header',
  MATRIX_CELL = 'matrix_cell',
  SEARCH_RESULTS_BUTTON = 'search_results_button',
  TEASER_BELOW_PHOTO_CAROUSEL = 'teaser_below_photo_carousel',
  DETAILS_POP_UP = 'details_pop_up',
}

export default defineComponent({
  name: "DisplayTab",
  props: {
    widgets: {
      type: Object,
    },
  },
  emits: ["updateWidget"],
  watch: {
    widgets: function (widgets: WidgetI) {
      this.widget = widgets;
      if (this.widget.config.module == "matrix") {
        this.matrix = true;
        this.matrixView();
      } else {
        this.matrix = false;
      }

      if (this.widget.config.module == "search") {
        this.search = true;
      } else {
        this.search = false;
      }

      if (this.widget.config.module == "matrixSearch") {
        this.matrixSearch = true;
        this.matrixView();
      } else {
        this.matrixSearch = false;
      }

      if (this.widget.config.module == "cardMatrix") {
        this.cardMatrix = true;
      } else {
        this.cardMatrix = false;
      }

      if (
        this.widget.config.available_text != null &&
        this.widget.config.matrix_mode == "availableText"
      ) {
        this.showAviable = true;
      }

      if (this.widget.config.show_property_thumbnail) {
        this.showPropertyThumbnail = true;
      } else {
        this.showPropertyThumbnail = false;
      }

      if (this.widget.config?.matrix_on_mobile) {
        this.displayMatrixOnMobile = this.widget.config.matrix_on_mobile;
      }

      if (this.widget.config?.deals_settings) {
        this.dealsSettings = this.widget.config.deals_settings;
      }

      if (this.widget.config.specials) {
        this.isSpecialsEnabled = this.widget.config.specials.enabled;
      }

      if (this.widget.config.show_property_filter !== undefined) {
        this.showPropertyFilter = this.widget.config.show_property_filter;
      } else {
        this.showPropertyFilter = false;
      }

      // Ensure display_days is always set with a default value
      if (!this.widget.config.display_days) {
        this.widget.config.display_days = 7;
      }

      // Ensure default_night_count is always set with a default value
      if (!this.widget.config.default_night_count) {
        this.widget.config.default_night_count = 7;
      }

      if (this.widget.config.card_matrix_show_matrix !== undefined) {
        this.cardMatrixShowMatrix = this.widget.config.card_matrix_show_matrix;
      } else {
        this.cardMatrixShowMatrix = true;
      }

      if (this.widget.config.card_matrix_show_matrix_on_load !== undefined) {
        this.cardMatrixShowMatrixOnLoad = !!this.widget.config.card_matrix_show_matrix_on_load;
      } else {
        this.cardMatrixShowMatrixOnLoad = false;
      }

      if (this.widget.config.card_matrix_hide_property_cards !== undefined) {
        this.cardMatrixShowPropertyBoards = !this.widget.config.card_matrix_hide_property_cards;
      } else {
        this.cardMatrixShowPropertyBoards = true;
      }

      if (this.widget.config.card_matrix_show_teaser_text !== undefined) {
        this.cardMatrixShowTeaserText = !!this.widget.config.card_matrix_show_teaser_text;
      } else {
        this.cardMatrixShowTeaserText = false;
      }

      if (this.widget.config.card_matrix_preload_properties !== undefined) {
        this.cardMatrixPreloadProperties = this.widget.config.card_matrix_preload_properties;
      } else {
        this.cardMatrixPreloadProperties = true;
      }

      if (this.widget.config.card_matrix_room_layout !== undefined) {
        this.cardMatrixRoomLayout = this.widget.config.card_matrix_room_layout;
      } else {
        this.cardMatrixRoomLayout = 'horizontal';
      }

      if (this.widget.config.card_matrix_show_search !== undefined) {
        this.cardMatrixShowSearch = !!this.widget.config.card_matrix_show_search;
      } else {
        this.cardMatrixShowSearch = true;
      }

      this.cardMatrixShowRoomCount = this.widget.config.matrix_show_room_count !== false;
      this.cardMatrixShowPrices = this.widget.config.matrix_show_prices !== false;
      this.cardMatrixShowAvailabilityText = this.widget.config.matrix_show_availability_text !== false;
      this.cardMatrixRoomCardHideRates = this.widget.config.room_card_hide_rates === true;
      this.cardMatrixShowTeaserPricing = this.widget.config.card_matrix_show_teaser_pricing !== false;
      this.cardMatrixMobileDefaultCollapsed = this.widget.config.matrix_mobile_default_collapsed === true;

      this.disableSpecialsDealsSettings();
    },
  },
  components: {
    CheckboxSlide,
    LayoutCard,
    NotificationPopup,
    ErrorIcon,
    SearchLayoutIcon,
    SearchMatrixLayoutIcon,
    MatrixLayoutIcon,
    CardMatrixLayoutIcon,
    DropdownMenu,
    BaseCheckbox,
    EditableDropdown,
  },
  data() {
    return {
      showPropertyThumbnail: true,
      isShowSavePopup: false,
      matrix_mode: MatrixDisplayDetailsE.availableText,
      search: true,
      matrixSearch: false,
      matrix: false,
      cardMatrix: false,
      widget: {} as WidgetI,
      showMatrixView: false,
      showAviable: false,
      showDays: [] as IShowDay[],
      showVariant: [] as IShowVariant[],
      maxLength: 10,
      errors: {
        availableText: "",
        unavailableText: "",
      } as ErrorI,
      changeCase: changeCase,
      ElementE: ElementE,
      MatrixDisplayDetailsE,
      displayMatrixOnMobile: false,
      DealsSettingsType,
      DealsSettingsLocation,
      dealsSettings: {
        specials: {
          matrix_row_header: false,
          matrix_cell: false,
          search_results_button: false,
          teaser_below_photo_carousel: false,
          details_pop_up: false,
        },
        minlos: {
          matrix_row_header: false,
          matrix_cell: false,
          search_results_button: false,
          teaser_below_photo_carousel: false,
          details_pop_up: false,
        },
      },
      isSpecialsEnabled: false,
      showPropertyFilter: false,
      cardMatrixShowMatrix: true,
      cardMatrixShowMatrixOnLoad: false,
      cardMatrixShowPropertyBoards: true,
      cardMatrixShowTeaserText: false,
      cardMatrixPreloadProperties: true,
      cardMatrixRoomLayout: 'horizontal' as string,
      cardMatrixShowSearch: true,
      cardMatrixShowRoomCount: true,
      cardMatrixShowPrices: true,
      cardMatrixShowAvailabilityText: true,
      cardMatrixRoomCardHideRates: false,
      cardMatrixShowTeaserPricing: true,
      cardMatrixMobileDefaultCollapsed: false,
    };
  },
  mounted() {
    this.showDays = showDays;
    this.showVariant = showVariant;
    this.showMatrixView = false;
    if (this.widget.config) {
      if (this.widget.config.show_property_thumbnail) {
        this.showPropertyThumbnail = this.widget.config.show_property_thumbnail;
      }
    }
  },
  methods: {
    validateField(fieldName: string, value: string) {
      if (!value) {
        this.errors[fieldName] = this.changeCase("the field should not be empty", this.ElementE.LABEL) as string;
      } else if (value.length > this.maxLength) {
        this.errors[fieldName] = this.changeCase(
          `the length of field must be less than ${this.maxLength} characters`,
          this.ElementE.LABEL,
        ) as string;
      } else {
        this.errors[fieldName] = "";
      }
    },

    showPropThumbnailChange(e: HtmlEvent) {
      this.widget.config.show_property_thumbnail = e.target.checked;
      this.showPropertyThumbnail = e.target.checked;
      this.updateWidget();
    },
    changeDisplayDays(days: string | number) {
      const daysValue = typeof days === 'string' ? parseInt(days) : days;
      
      if (isNaN(daysValue) || daysValue < 1 || daysValue > 15) {
        return;
      }
      this.widget.config.display_days = daysValue;
      this.updateWidget();
    },
    changeDefaultNightCount(nights: string | number) {
      const nightsValue = typeof nights === 'string' ? parseInt(nights) : nights;
      
      if (isNaN(nightsValue) || nightsValue < 1 || nightsValue > 15) {
        return;
      }
      this.widget.config.default_night_count = nightsValue;
      this.updateWidget();
    },
    updateWidget() {
      this.isShowSavePopup = true;
      this.$emit("updateWidget", this.widget);
    },
    selectLayout(layoutId: string) {
      this.search = false;
      this.matrix = false;
      this.matrixSearch = false;
      this.cardMatrix = false;

      if (layoutId == "search") {
        this.search = true;
        this.displayMatrixOnMobile = false;
        this.matrixViewHide();
      }
      if (layoutId == "matrix") {
        this.matrix = true;
        this.matrixView();
      }
      if (layoutId == "matrixSearch") {
        this.matrixSearch = true;
        this.displayMatrixOnMobile = false;
        this.matrixView();
      }
      if (layoutId == "cardMatrix") {
        this.cardMatrix = true;
        this.displayMatrixOnMobile = false;
        this.matrixViewHide();
      }

      this.widget.config.matrix_on_mobile = this.displayMatrixOnMobile;
      this.widget.config.module = layoutId;
      this.updateWidget();
    },
    showAviableField(id: string) {
      this.showAviable = id === 'availableText';
      this.widget.config.matrix_mode = id;
      this.updateWidget();
    },
    matrixView() {
      this.showMatrixView = true;
    },
    matrixViewHide() {
      this.showMatrixView = false;
    },
    enableDisplayMatrixOnMobile(value: boolean) {
      this.displayMatrixOnMobile = value;
      this.widget.config.matrix_on_mobile = this.displayMatrixOnMobile;
      this.updateWidget();
    },
    updateDealsSettings(value: boolean, type: DealsSettingsType, location: DealsSettingsLocation) {
      this.dealsSettings[type][location] = value;
      this.widget.config = {
        ...this.widget.config,
        deals_settings: this.dealsSettings,
      };
      this.updateWidget();
    },
    disableSpecialsDealsSettings() {
      if (this.isSpecialsEnabled) return;
      this.dealsSettings.specials.matrix_row_header = false;
      this.dealsSettings.specials.matrix_cell = false;
      this.dealsSettings.specials.search_results_button = false;
      this.dealsSettings.specials.teaser_below_photo_carousel = false;
      this.dealsSettings.specials.details_pop_up = false;

      this.widget.config = {
        ...this.widget.config,
        deals_settings: this.dealsSettings,
      };
    },
    enableShowPropertyFilter(value: boolean) {
      this.showPropertyFilter = value;
      this.widget.config.show_property_filter = value;
      this.updateWidget();
    },
    toggleCardMatrixShowMatrix(value: boolean) {
      this.cardMatrixShowMatrix = value;
      this.widget.config.card_matrix_show_matrix = value;
      if (!value) {
        this.cardMatrixShowMatrixOnLoad = false;
        this.widget.config.card_matrix_show_matrix_on_load = false;
        this.cardMatrixMobileDefaultCollapsed = false;
        this.widget.config.matrix_mobile_default_collapsed = false;
      }
      this.updateWidget();
    },
    toggleCardMatrixShowMatrixOnLoad(value: boolean) {
      this.cardMatrixShowMatrixOnLoad = value;
      this.widget.config.card_matrix_show_matrix_on_load = value;
      this.updateWidget();
    },
    toggleCardMatrixShowPropertyBoards(value: boolean) {
      this.cardMatrixShowPropertyBoards = value;
      this.widget.config.card_matrix_hide_property_cards = !value;
      if (value) {
        this.cardMatrixShowTeaserText = false;
        this.widget.config.card_matrix_show_teaser_text = false;
      }
      this.updateWidget();
    },
    toggleCardMatrixShowTeaserText(value: boolean) {
      this.cardMatrixShowTeaserText = value;
      this.widget.config.card_matrix_show_teaser_text = value;
      this.updateWidget();
    },
    toggleCardMatrixPreloadProperties(value: boolean) {
      this.cardMatrixPreloadProperties = value;
      this.widget.config.card_matrix_preload_properties = value;
      this.updateWidget();
    },
    toggleCardMatrixShowSearch(value: boolean) {
      this.cardMatrixShowSearch = value;
      this.widget.config.card_matrix_show_search = value;
      if (!value) {
        this.cardMatrixShowPropertyBoards = true;
        this.widget.config.card_matrix_hide_property_cards = false;
        this.cardMatrixPreloadProperties = true;
        this.widget.config.card_matrix_preload_properties = true;
      }
      this.updateWidget();
    },
    setCardMatrixRoomLayout(value: string) {
      this.cardMatrixRoomLayout = value;
      this.widget.config.card_matrix_room_layout = value;
      this.updateWidget();
    },
    toggleCardMatrixShowRoomCount(value: boolean) {
      this.cardMatrixShowRoomCount = value;
      this.widget.config.matrix_show_room_count = value;
      this.updateWidget();
    },
    toggleCardMatrixShowPrices(value: boolean) {
      this.cardMatrixShowPrices = value;
      this.widget.config.matrix_show_prices = value;
      this.updateWidget();
    },
    toggleCardMatrixShowAvailabilityText(value: boolean) {
      // Key kept as matrix_show_availability_text (pre-rename): the label reads
      // "Show sold-out label in matrix" because that is all this flag gates, but
      // renaming the key would need a config migration for no functional gain.
      this.cardMatrixShowAvailabilityText = value;
      this.widget.config.matrix_show_availability_text = value;
      this.updateWidget();
    },
    toggleCardMatrixRoomCardHideRates(value: boolean) {
      this.cardMatrixRoomCardHideRates = value;
      this.widget.config.room_card_hide_rates = value;
      this.updateWidget();
    },
    toggleCardMatrixShowTeaserPricing(value: boolean) {
      this.cardMatrixShowTeaserPricing = value;
      this.widget.config.card_matrix_show_teaser_pricing = value;
      this.updateWidget();
    },
    toggleCardMatrixMobileDefaultCollapsed(value: boolean) {
      this.cardMatrixMobileDefaultCollapsed = value;
      this.widget.config.matrix_mobile_default_collapsed = value;
      this.updateWidget();
    },
  },
});
</script>
